import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { assessSituation, generateFAQs, SourceDocuments, FAQItem } from '../../utils/faqApi';

interface ComparisonResult {
  topic: string;
  version: string;
  determination: string;
  confidence: number;
  reasoning: string;
  citations: any[];
  recommendation: string;
  loading: boolean;
  error?: string;
  blocked?: boolean;
  sourceDocuments?: SourceDocuments;
}

interface FAQsState {
  faqs: FAQItem[];
  loading: boolean;
  error?: string;
}

const ForeignContactComparison: React.FC = () => {
  const [question, setQuestion] = useState<string>('');
  const [results, setResults] = useState<{
    original: ComparisonResult | null;
    sead3: ComparisonResult | null;
  }>({
    original: null,
    sead3: null
  });

  const [faqsState, setFaqsState] = useState<{
    original: FAQsState;
    sead3: FAQsState;
  }>({
    original: { faqs: [], loading: true },
    sead3: { faqs: [], loading: true }
  });

  // Load FAQs on component mount
  useEffect(() => {
    const loadFAQs = async () => {
      try {
        const [originalFAQs, sead3FAQs] = await Promise.all([
          generateFAQs('foreign-contact-reporting', 5, undefined, undefined, false),
          generateFAQs('foreign-contact-reporting-sead3', 5, undefined, undefined, false)
        ]);

        setFaqsState({
          original: {
            faqs: originalFAQs.guidance?.faqs || [],
            loading: false,
            error: originalFAQs.success ? undefined : 'Failed to load FAQs'
          },
          sead3: {
            faqs: sead3FAQs.guidance?.faqs || [],
            loading: false,
            error: sead3FAQs.success ? undefined : 'Failed to load FAQs'
          }
        });
      } catch (error) {
        console.error('Error loading FAQs:', error);
        setFaqsState({
          original: { faqs: [], loading: false, error: 'Failed to load FAQs' },
          sead3: { faqs: [], loading: false, error: 'Failed to load FAQs' }
        });
      }
    };

    loadFAQs();
  }, []);

  const handleCompare = async () => {
    if (!question.trim()) return;

    // Reset results and set loading
    setResults({
      original: {
        topic: 'foreign-contact-reporting',
        version: 'Original (Decision Tree)',
        determination: '',
        confidence: 0,
        reasoning: '',
        citations: [],
        recommendation: '',
        loading: true
      },
      sead3: {
        topic: 'foreign-contact-reporting-sead3',
        version: 'SEAD-3 (Policy)',
        determination: '',
        confidence: 0,
        reasoning: '',
        citations: [],
        recommendation: '',
        loading: true
      }
    });

    // Call both versions in parallel
    try {
      const [originalResponse, sead3Response] = await Promise.all([
        assessSituation(question, 'foreign-contact-reporting'),
        assessSituation(question, 'foreign-contact-reporting-sead3')
      ]);

      setResults({
        original: {
          topic: 'foreign-contact-reporting',
          version: 'Original (Decision Tree)',
          determination: originalResponse.guidance?.assessment?.determination || 'No response',
          confidence: originalResponse.guidance?.assessment?.confidence || 0,
          reasoning: originalResponse.guidance?.assessment?.reasoning || '',
          citations: originalResponse.guidance?.assessment?.citations || [],
          recommendation: originalResponse.guidance?.assessment?.recommendation || '',
          loading: false,
          blocked: originalResponse.blocked,
          sourceDocuments: originalResponse.source_documents
        },
        sead3: {
          topic: 'foreign-contact-reporting-sead3',
          version: 'SEAD-3 (Policy)',
          determination: sead3Response.guidance?.assessment?.determination || 'No response',
          confidence: sead3Response.guidance?.assessment?.confidence || 0,
          reasoning: sead3Response.guidance?.assessment?.reasoning || '',
          citations: sead3Response.guidance?.assessment?.citations || [],
          recommendation: sead3Response.guidance?.assessment?.recommendation || '',
          loading: false,
          blocked: sead3Response.blocked,
          sourceDocuments: sead3Response.source_documents
        }
      });
    } catch (error) {
      console.error('Comparison error:', error);
      setResults({
        original: {
          topic: 'foreign-contact-reporting',
          version: 'Original',
          determination: '',
          confidence: 0,
          reasoning: '',
          citations: [],
          recommendation: '',
          loading: false,
          error: 'Failed to get response'
        },
        sead3: {
          topic: 'foreign-contact-reporting-sead3',
          version: 'SEAD-3',
          determination: '',
          confidence: 0,
          reasoning: '',
          citations: [],
          recommendation: '',
          loading: false,
          error: 'Failed to get response'
        }
      });
    }
  };

  const getCitationInfo = (citation: any, sourceDocuments?: SourceDocuments): { text: string; uri: string | null } => {
    if (typeof citation === 'object' && 'source_id' in citation && sourceDocuments) {
      const doc = sourceDocuments[citation.source_id];
      const displayName = doc?.display_name || doc?.document_name || 'Unknown Source';
      const pageText = citation.page ? `, page ${citation.page}` : '';
      return {
        text: `${displayName}${pageText}`,
        uri: doc?.uri || null
      };
    }
    return {
      text: typeof citation === 'string' ? citation : citation.text || 'Unknown',
      uri: null
    };
  };

  const renderResult = (result: ComparisonResult | null, side: 'left' | 'right') => {
    if (!result) {
      return (
        <div className="text-center py-12 text-gray-400">
          <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>Ask a question to see results</p>
        </div>
      );
    }

    if (result.loading) {
      return (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Analyzing with {result.version}...</p>
        </div>
      );
    }

    if (result.error) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <p className="text-red-800">{result.error}</p>
        </div>
      );
    }

    const bgColor = side === 'left' ? 'bg-blue-50' : 'bg-purple-50';
    const borderColor = side === 'left' ? 'border-blue-200' : 'border-purple-200';
    const textColor = side === 'left' ? 'text-blue-900' : 'text-purple-900';

    return (
      <div className={`${bgColor} border ${borderColor} rounded-lg p-6`}>
        {result.blocked && (
          <div className="flex items-start mb-4 bg-amber-100 border border-amber-300 rounded p-3">
            <svg className="w-4 h-4 text-amber-600 mr-2 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="text-xs text-amber-800">Content Restriction Applied</span>
          </div>
        )}

        <div className="mb-4">
          <span className="text-xs font-medium text-gray-700">Determination:</span>
          <p className={`text-sm font-medium ${textColor} mt-1`}>{result.determination}</p>
        </div>

        {result.confidence > 0 && (
          <div className="mb-4">
            <span className="text-xs font-medium text-gray-700">Confidence:</span>
            <div className="flex items-center mt-1">
              <span className={`text-sm font-medium ${
                result.confidence >= 0.8 ? 'text-green-600' :
                result.confidence >= 0.6 ? 'text-yellow-600' : 'text-red-600'
              }`}>
                {Math.round(result.confidence * 100)}%
              </span>
              <div className="ml-3 flex-1 bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full ${
                    result.confidence >= 0.8 ? 'bg-green-600' :
                    result.confidence >= 0.6 ? 'bg-yellow-600' : 'bg-red-600'
                  }`}
                  style={{ width: `${result.confidence * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <span className="text-xs font-medium text-gray-700">Reasoning:</span>
          <p className="text-sm text-gray-800 mt-1">{result.reasoning}</p>
        </div>

        {result.recommendation && (
          <div className="mb-4 p-3 bg-white bg-opacity-50 rounded border border-gray-300">
            <span className="text-xs font-medium text-gray-700">Recommendation:</span>
            <p className="text-sm text-gray-800 mt-1">{result.recommendation}</p>
          </div>
        )}

        {result.citations.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-300">
            <span className="text-xs font-medium text-gray-700">Citations:</span>
            <ul className="mt-2 space-y-1">
              {result.citations.map((citation, idx) => {
                const { text, uri } = getCitationInfo(citation, result.sourceDocuments);
                return uri ? (
                  <li key={idx}>
                    <a
                      href={uri}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-800 underline"
                    >
                      {text}
                    </a>
                  </li>
                ) : (
                  <li key={idx} className="text-xs text-gray-700">{text}</li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <Link to="/rag-test" className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-4">
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to RAG Testing Dashboard
        </Link>

        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Foreign Contact Reporting - A/B Comparison</h1>
          <p className="text-gray-600 mb-6">
            Test the same question against both versions of the Foreign Contact FAQ system to compare quality,
            accuracy, and relevance of responses.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-sm font-medium text-blue-900 mb-2">Original Version</h3>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li>• Source: FINAL SEAD 3 Contact and Relationship Reporting Exercise.pdf</li>
                  <li>• Format: Decision-tree training document</li>
                  <li>• Pages: 5</li>
                  <li>• Style: Educational, step-by-step</li>
                </ul>
              </div>
              <div>
                <h3 className="text-sm font-medium text-purple-900 mb-2">SEAD-3 Version</h3>
                <ul className="text-xs text-purple-800 space-y-1">
                  <li>• Source: SEAD-3-Reporting-U.pdf Section F.2</li>
                  <li>• Format: Authoritative policy document</li>
                  <li>• Pages: 11 (comprehensive)</li>
                  <li>• Style: Regulatory, comprehensive</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Pre-Generated FAQs Comparison */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Pre-Generated FAQs by Source</h2>
          <p className="text-gray-600 mb-6">
            Compare how each source document approaches common questions about foreign contact reporting.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Original FAQs */}
            <div>
              <div className="bg-blue-100 border border-blue-300 rounded-t-lg p-3">
                <h3 className="text-lg font-semibold text-blue-900">Original Version FAQs</h3>
                <p className="text-xs text-blue-700 mt-1">Training Document</p>
              </div>
              <div className="bg-white border border-blue-200 rounded-b-lg p-6">
                {faqsState.original.loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading FAQs...</p>
                  </div>
                ) : faqsState.original.error ? (
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <p className="text-sm text-red-800">{faqsState.original.error}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {faqsState.original.faqs.map((faq, idx) => (
                      <details key={idx} className="group">
                        <summary className="cursor-pointer text-sm font-medium text-blue-900 hover:text-blue-700 list-none flex items-start">
                          <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 transform group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="flex-1">{faq.question}</span>
                        </summary>
                        <div className="mt-2 ml-6 text-sm text-gray-700 bg-blue-50 p-3 rounded">
                          {faq.answer}
                        </div>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* SEAD-3 FAQs */}
            <div>
              <div className="bg-purple-100 border border-purple-300 rounded-t-lg p-3">
                <h3 className="text-lg font-semibold text-purple-900">SEAD-3 Version FAQs</h3>
                <p className="text-xs text-purple-700 mt-1">Policy Document</p>
              </div>
              <div className="bg-white border border-purple-200 rounded-b-lg p-6">
                {faqsState.sead3.loading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2"></div>
                    <p className="text-sm text-gray-600">Loading FAQs...</p>
                  </div>
                ) : faqsState.sead3.error ? (
                  <div className="bg-red-50 border border-red-200 rounded p-4">
                    <p className="text-sm text-red-800">{faqsState.sead3.error}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {faqsState.sead3.faqs.map((faq, idx) => (
                      <details key={idx} className="group">
                        <summary className="cursor-pointer text-sm font-medium text-purple-900 hover:text-purple-700 list-none flex items-start">
                          <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0 transform group-open:rotate-90 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="flex-1">{faq.question}</span>
                        </summary>
                        <div className="mt-2 ml-6 text-sm text-gray-700 bg-purple-50 p-3 rounded">
                          {faq.answer}
                        </div>
                      </details>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Question Input Section */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Ask Your Own Question</h2>
          <p className="text-gray-600 mb-6">
            Test the same question against both versions to compare how each interprets and responds.
          </p>

          {/* Question Input */}
          <div className="mb-6">
            <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-2">
              Foreign Contact Question
            </label>
            <textarea
              id="question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Example: I have a friend from Germany who I email monthly about software projects. Do I need to report this contact?"
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <button
              onClick={handleCompare}
              disabled={!question.trim() || (results.original?.loading || results.sead3?.loading)}
              className="mt-3 w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              {(results.original?.loading || results.sead3?.loading) ? 'Analyzing...' : 'Compare Both Versions'}
            </button>
          </div>

          {/* Suggested Test Questions */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Suggested Test Questions:</h3>
            <div className="space-y-2">
              {[
                "I have a colleague from France who I exchange work emails with. Is this reportable?",
                "My roommate is an international student from China. Do I need to report this?",
                "I'm friends with someone from Russia on social media. Do I need to report this?",
                "I met someone at a conference who works for a foreign government. We exchanged business cards.",
                "My neighbor is from Mexico and we occasionally have dinner together. Is this reportable?"
              ].map((q, idx) => (
                <button
                  key={idx}
                  onClick={() => setQuestion(q)}
                  className="block w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded border border-blue-200 hover:border-blue-400 transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Side-by-Side Assessment Results */}
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Comparison Results</h2>
        <p className="text-gray-600 mb-6">
          View how each version responds to your question side-by-side.
        </p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Original Version */}
          <div>
            <div className="bg-blue-100 border border-blue-300 rounded-t-lg p-3">
              <h2 className="text-lg font-semibold text-blue-900 flex items-center">
                <span className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">
                  A
                </span>
                Original Version
              </h2>
              <p className="text-xs text-blue-800 mt-1">Decision Tree Document</p>
            </div>
            <div className="bg-white border border-blue-200 rounded-b-lg p-6">
              {renderResult(results.original, 'left')}
            </div>
          </div>

          {/* SEAD-3 Version */}
          <div>
            <div className="bg-purple-100 border border-purple-300 rounded-t-lg p-3">
              <h2 className="text-lg font-semibold text-purple-900 flex items-center">
                <span className="w-8 h-8 bg-purple-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">
                  B
                </span>
                SEAD-3 Version
              </h2>
              <p className="text-xs text-purple-800 mt-1">Policy Document</p>
            </div>
            <div className="bg-white border border-purple-200 rounded-b-lg p-6">
              {renderResult(results.sead3, 'right')}
            </div>
          </div>
        </div>

        {/* Comparison Notes */}
        {results.original && results.sead3 && !results.original.loading && !results.sead3.loading && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-6">
            <h3 className="text-sm font-medium text-amber-900 mb-3">Comparison Analysis Tips</h3>
            <ul className="text-sm text-amber-800 space-y-2">
              <li>• Compare the <strong>specificity</strong> of responses - which provides more actionable guidance?</li>
              <li>• Check <strong>citation quality</strong> - which references are more precise?</li>
              <li>• Evaluate <strong>confidence levels</strong> - which version is more certain in its assessment?</li>
              <li>• Consider <strong>reasoning depth</strong> - which explanation is more comprehensive?</li>
              <li>• Note any <strong>contradictions</strong> - do they give different answers to the same question?</li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForeignContactComparison;
